import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import Layout from '../components/layout/Layout';
import { Spinner } from '../components/common';
import { useWindowWidth } from '../hooks/useWindowWidth';

const tipizateAPI = {
  list:     ()   => api.get('/tipizate').then(r => r.data),
  download: (id) => api.get(`/tipizate/${id}/download`).then(r => r.data),
};

const TIP_ICON = { pdf: '📄', docx: '📝', doc: '📝', odt: '📝' };
const TIP_COLOR = { pdf: '#e24b4a', docx: '#2b579a', doc: '#2b579a', odt: '#1a73e8' };

const TIP_CERERE_LABEL = {
  sesizare:   'Sesizare',
  solicitare: 'Solicitare',
  petitie:    'Petiție',
  reclamatie: 'Reclamație',
  informatie: 'Cerere de informații',
};

export default function FormularePage() {
  const w = useWindowWidth();
  const isMobile = w < 640;
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(null);

  const { data, isLoading } = useQuery('tipizate', tipizateAPI.list, { staleTime: 60000 });

  const toate  = data?.data || [];
  const grupe  = data?.grupe || {};

  // Filtrare locală după search
  const filtrate = search.trim()
    ? toate.filter(t =>
        t.titlu.toLowerCase().includes(search.toLowerCase()) ||
        (t.descriere || '').toLowerCase().includes(search.toLowerCase()) ||
        t.categorie.toLowerCase().includes(search.toLowerCase())
      )
    : null;

  async function handleDownload(tipizat) {
    setDownloading(tipizat.id);
    try {
      const { url, fisierNume } = await tipizateAPI.download(tipizat.id);
      // Descărcăm fișierul
      const a = document.createElement('a');
      a.href = url;
      a.download = fisierNume;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`${tipizat.titlu} — descărcat`);
    } catch {
      toast.error('Eroare la descărcare. Încercați din nou.');
    } finally {
      setDownloading(null);
    }
  }

  return (
    <Layout>
      <div style={{ maxWidth: '900px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 6px' }}>
            Formulare și tipizate
          </h1>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 16px' }}>
            Descărcați formularele necesare, completați-le și atașați-le la cererea dvs.
          </p>

          {/* Search */}
          <input
            type="search"
            placeholder="Căutați un formular..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '11px 16px', fontSize: '15px',
              border: '1.5px solid #e5e7eb', borderRadius: '10px',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {isLoading && <Spinner message="Se încarcă formularele..." />}

        {!isLoading && toate.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📂</div>
            <p>Nu există formulare disponibile momentan.</p>
            <p style={{ fontSize: '13px' }}>Contactați primăria pentru mai multe informații.</p>
          </div>
        )}

        {/* Rezultate căutare */}
        {filtrate && (
          filtrate.length === 0
            ? <p style={{ color: '#888', padding: '20px 0' }}>Niciun formular găsit pentru „{search}".</p>
            : <div style={gridStyle(isMobile)}>
                {filtrate.map(t => <FormularCard key={t.id} tipizat={t} onDownload={handleDownload} downloading={downloading} />)}
              </div>
        )}

        {/* Afișare pe categorii */}
        {!filtrate && Object.keys(grupe).sort().map(cat => (
          <div key={cat} style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #e5e7eb' }}>
              {cat}
              <span style={{ fontSize: '12px', fontWeight: '400', color: '#9ca3af', marginLeft: '8px' }}>
                {grupe[cat].length} {grupe[cat].length === 1 ? 'formular' : 'formulare'}
              </span>
            </h2>
            <div style={gridStyle(isMobile)}>
              {grupe[cat].map(t => (
                <FormularCard key={t.id} tipizat={t} onDownload={handleDownload} downloading={downloading} />
              ))}
            </div>
          </div>
        ))}

        {/* Info */}
        {!isLoading && toate.length > 0 && (
          <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '14px 16px', fontSize: '13px', color: '#1d4ed8', marginTop: '8px' }}>
            💡 Descărcați formularul, completați-l și atașați-l când depuneți o cerere online sau la ghișeu.
          </div>
        )}
      </div>
    </Layout>
  );
}

function FormularCard({ tipizat, onDownload, downloading }) {
  const isLoading = downloading === tipizat.id;
  const icon  = TIP_ICON[tipizat.fisier_tip]  || '📄';
  const color = TIP_COLOR[tipizat.fisier_tip] || '#555';

  return (
    <div style={cardStyle}>
      {/* Icon tip fișier */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{ fontSize: '28px', flexShrink: 0, lineHeight: 1 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: '600', color: '#1a1a2e', fontSize: '14px', marginBottom: '4px', lineHeight: '1.4' }}>
            {tipizat.titlu}
          </div>
          {tipizat.descriere && (
            <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5', marginBottom: '8px' }}>
              {tipizat.descriere}
            </div>
          )}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <span style={{ ...badge, background: color + '15', color }}>
              {tipizat.fisier_tip?.toUpperCase()}
            </span>
            {tipizat.tip_cerere && (
              <span style={{ ...badge, background: '#f0fdf4', color: '#15803d' }}>
                {TIP_CERERE_LABEL[tipizat.tip_cerere] || tipizat.tip_cerere}
              </span>
            )}
            {tipizat.fisier_marime && (
              <span style={{ ...badge, background: '#f9fafb', color: '#6b7280' }}>
                {(tipizat.fisier_marime / 1024).toFixed(0)} KB
              </span>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={() => onDownload(tipizat)}
        disabled={isLoading}
        style={downloadBtn(isLoading)}
      >
        {isLoading ? '⏳ Se descarcă...' : '⬇ Descarcă formular'}
      </button>

      {tipizat.descarcat_ori > 0 && (
        <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'right', marginTop: '6px' }}>
          {tipizat.descarcat_ori} descărcări
        </div>
      )}
    </div>
  );
}

// ── Stiluri ───────────────────────────────────────────────────
const gridStyle = (isMobile) => ({
  display: 'grid',
  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '14px',
});

const cardStyle = {
  background: '#fff',
  border: '1.5px solid #e5e7eb',
  borderRadius: '12px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
};

const badge = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '6px',
  fontSize: '11px',
  fontWeight: '600',
};

const downloadBtn = (disabled) => ({
  marginTop: 'auto',
  padding: '9px 0',
  background: disabled ? '#f3f4f6' : '#2d2d7e',
  color: disabled ? '#9ca3af' : '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontWeight: '600',
  fontSize: '13px',
  width: '100%',
});
